import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

import { acquisitionLabelMaxLength } from '../../app.constants';
import { InfoEditComponent } from '../../files/info-edit/info-edit.component';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerProjectId, getRoleMatrix } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Acquisition } from '../../shared/models/acquisition.model';
import { firstValueFrom } from '../../shared/observable.utils';

export interface AcquisitionInfoDialogData {
  acquisition: Acquisition;
}

export interface AcquisitionInfoDialogResult {
  dirty: boolean
}

@Component({
  host: {
    class: 'mat-dialog-component',
  },
  selector: 'acquisition-info-dialog',
  templateUrl: './acquisition-info-dialog.component.html',
  styleUrls: ['./acquisition-info-dialog.component.scss'],
})
export class AcquisitionInfoDialog {
  @ViewChild(InfoEditComponent, { static: false }) customInfo: InfoEditComponent

  acquisition: Acquisition = this.data.acquisition
  permissions = {
    canEditMetadata: false,
    viewTags: false,
  }
  error: Error = null
  form: FormGroup = new FormGroup({
    label: new FormControl(this.acquisition?.label || null, [Validators.required, Validators.maxLength(acquisitionLabelMaxLength)]),
  })
  acquisitionLabelMaxLength = acquisitionLabelMaxLength

  private subscriptions = {
    permissions: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AcquisitionInfoDialogData,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    public ref: MatDialogRef<AcquisitionInfoDialog, AcquisitionInfoDialogResult>,
  ) {
    this.getAcquisition();
  }

  getAcquisition(): void {
    this.error = null;
    this.coreApi.acquisitions
      .get(this.acquisition._id)
      .subscribe({
        next: (acquisition) => {
          this.acquisition = acquisition;
          this.form.setValue({
            label: this.acquisition?.label || null,
          });
          this.updatePermissions();
        },
        error: (error: HttpErrorResponse) => {
          this.logger.error(error);
          this.error = error;
        },
      });
  }

  updatePermissions(): void {
    const projectId = getContainerProjectId(this.acquisition);
    this.subscriptions.permissions.unsubscribe();

    if (projectId) {
      this.subscriptions.permissions = this.projectsService
        .getProjectPermissions(projectId)
        .subscribe((projectPermissions) => {
          this.permissions = {
            canEditMetadata: projectPermissions.includes(PermissionAction.ContainersModifyMetadata),
            viewTags: projectPermissions.includes(PermissionAction.TagsView),
          };
        });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.subscriptions.permissions = this.profileService
        .getProfile()
        .pipe(first())
        .subscribe((profile) => {
          const roleMatrix = getRoleMatrix(this.acquisition, profile);
          this.permissions = {
            canEditMetadata: roleMatrix[ContainerAccess.ReadWrite],
            viewTags: roleMatrix[ContainerAccess.ReadOnly],
          };
        });
    }
  }

  close(): void {
    this.ref.close({ dirty: false });
  }

  async save(): Promise<void> {
    if (this.form.invalid || this.customInfo?.invalid) {
      this.customInfo && this.customInfo.saveFields(); // reveal errors
      return;
    }

    const formData = this.form.value;

    if (this.acquisition.label !== formData.label) {
      try {
        await firstValueFrom(
          this.coreApi.acquisitions.put(this.acquisition._id, {
            label: formData.label,
          })
        );
      } catch (error) {
        if (error.status === 409) {
          this.notifications.error('acquisition.create.duplicate');
        } else {
          this.logger.error(error);
          this.notifications.error('acquisition.rename.error');
        }
        return;
      }
    }

    if (this.customInfo?.dirty) {
      try {
        await this.customInfo.saveFields();
      } catch (error) {
        this.notifications.error('container.info.update.error');
        return;
      }
    }

    this.ref.close({ dirty: true });
  }
}
